import { apiObjUrl } from "src/common/apiUrl";
import { restWithObjectVersion } from "src/common/restWithObjectVersion";
import { ZAttribute, zAttribute } from "src/types/ZAttribute";
import { ZIdName } from "src/types/ZIdName";
import { ZObjectSummary, zObjectSummary } from "src/types/ZObjectSummary";
import { ZUid } from "src/types/zUid";

/**
 * Lightweight-каталог объектов для дерева Управления.
 *
 * GET /ms-object/api/v3/objects -> ObjectNameDto[]
 *
 * Осознанные ограничения:
 * - используется restWithObjectVersion, чтобы ключ кэша учитывал версию модели
 *   и пользователя (так же, как у полного /objects);
 * - перевод не запрашивается сознательно: имя объекта здесь - ключ, по которому
 *   loadTranslation/saveTranslation находят запись переводов, и переведённое имя
 *   в форме перезаписало бы базовое при сохранении;
 * - никакого автоматического fallback на loadObjects(): падение v3 должно быть
 *   видимой ошибкой, а не молчаливым возвратом к загрузке полного графа;
 * - ошибки Zod и сети пробрасываются наверх;
 * - сортировка не выполняется, порядок ответа backend не гарантирован
 *   и приводится к общему виду уже в дереве.
 */
export const loadObjectSummaries = async (): Promise<ZObjectSummary[]> => {
  const response = await restWithObjectVersion.get(apiObjUrl("/v3/objects"));
  return zObjectSummary.array().parse(response.data);
};

const loadAttribute = async (attributeId: ZUid): Promise<ZAttribute> => {
  const response = await restWithObjectVersion.get(
    apiObjUrl(`/attributes/${attributeId}`),
  );
  return zAttribute.parse(response.data);
};

/**
 * Значения справочника, на который ссылается атрибут группы.
 *
 * Отличие от общего loadFromAttr в том, что ошибка пробрасывается, а не
 * превращается в пустой список: пустой справочник и неудачный запрос - разные
 * вещи, и во втором случае ветвь нельзя считать загруженной, иначе она молча
 * станет листом.
 */
export const loadDictionaryValues = async (
  attributeId: ZUid,
): Promise<ZIdName[]> => {
  const attr = await loadAttribute(attributeId);
  const { referenceId } = attr;
  if (!referenceId) throw Error(`Атрибут ${attr.name} не содержит справочника`);
  const dictionary = await loadAttribute(referenceId);
  return (dictionary.values ?? []).map(({ id, value, displayId }) => ({
    id,
    name: String(value),
    displayId,
  }));
};
